/**
 * Reports & Analytics — a first-class dashboard widget (Admin/HR only).
 *
 * Cross-module overview: date-range filter, four stacked report cards
 * (attendance, leave, workspace, headcount), PDF export (print) and saved
 * views (per-user presets). Rendered inside the standard `.dash-widget` glass
 * shell, so it inherits the dashboard's glassmorphism + corner radius and is
 * draggable / reorderable in Personalise mode like any other widget.
 */
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  BarChart3, Printer, Save, Clock, CalendarDays,
  Banknote, Users, Trash2, ArrowRight,
} from "lucide-react";
import api from "../../api/axios";
import { T } from "../../components/em/ui";
import WidgetHeader from "../dashboard/widgets/WidgetHeader";
import "./reports.css";

const money = (n) => "₹" + Math.round(Number(n) || 0).toLocaleString("en-IN");
const todayStr = () => new Date().toISOString().slice(0, 10);
const yearStartStr = () => `${new Date().getFullYear()}-01-01`;
const fmtRange = (a, b) => {
  const f = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return `${f(a)} → ${f(b)}`;
};

const Card = ({ icon: Icon, title, accent, children }) => (
  <div className="report-card">
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <span style={{ width: 30, height: 30, borderRadius: 8, background: (accent || T.orange) + "1f", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={15} color={accent || T.orange} />
      </span>
      <span style={{ font: `700 14px ${T.font}`, color: T.fg1 }}>{title}</span>
    </div>
    {children}
  </div>
);

const Kpi = ({ label, value, color }) => (
  <div>
    <div style={{ font: `800 20px/1 ${T.font}`, color: color || T.fg1, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{value}</div>
    <div style={{ font: `500 11px ${T.font}`, color: T.fg7, marginTop: 4 }}>{label}</div>
  </div>
);
const KpiRow = ({ items }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 12, marginBottom: 14 }}>
    {items.map((k) => <Kpi key={k.label} {...k} />)}
  </div>
);

const BarList = ({ rows, max, color, fmt }) => {
  const m = max || Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <div style={{ font: `400 12px ${T.font}`, color: T.fg7 }}>No data in this range.</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      {rows.map((r) => (
        <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ font: `500 12px ${T.font}`, color: T.fg3, width: 110, minWidth: 110, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.label}</span>
          <div className="report-bar-track">
            <div className="report-bar-fill" style={{ width: `${Math.max(2, (r.value / m) * 100)}%`, background: r.color || color || T.orange }} />
          </div>
          <span style={{ font: `700 12px ${T.font}`, color: T.fg1, width: 70, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt ? fmt(r.value) : r.value}</span>
        </div>
      ))}
    </div>
  );
};

// Tiny sparkline of a daily series.
const Spark = ({ points, color }) => {
  if (!points || points.length < 2) return null;
  const w = 320, h = 40;
  const max = Math.max(1, ...points.map((p) => p.v));
  const line = points.map((p, i) => `${(i / (points.length - 1)) * w},${h - (p.v / max) * h}`).join(" ");
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: "block", marginTop: 10 }}>
      <polyline points={line} fill="none" stroke={color || T.orange} strokeWidth="2" />
    </svg>
  );
};

export default function ReportsPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [from, setFrom] = useState(yearStartStr());
  const [to, setTo] = useState(todayStr());
  const [views, setViews] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/overview?from=${from}&to=${to}`);
      setData(res.data);
    } catch {
      toast.error("Could not load reports");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  const loadViews = useCallback(async () => {
    try { const res = await api.get("/reports/views"); setViews(res.data || []); } catch {}
  }, []);

  // Always-on widget: load on mount + whenever the range changes; views once.
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadViews(); }, [loadViews]);

  const exportPdf = () => {
    document.body.classList.add("report-print-mode");
    const cleanup = () => {
      document.body.classList.remove("report-print-mode");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  };

  const saveView = async () => {
    const name = window.prompt("Name this report view:", "");
    if (!name || !name.trim()) return;
    try {
      await api.post("/reports/views", { name: name.trim(), config: { from, to } });
      toast.success("View saved");
      loadViews();
    } catch { toast.error("Could not save view"); }
  };

  const applyView = (id) => {
    const v = views.find((x) => String(x.id) === String(id));
    if (v?.config?.from) setFrom(v.config.from);
    if (v?.config?.to) setTo(v.config.to);
  };

  const deleteView = async (id) => {
    try { await api.delete(`/reports/views/${id}`); toast.success("View deleted"); loadViews(); }
    catch { toast.error("Could not delete view"); }
  };

  const att = data?.attendance;
  const lv = data?.leave;
  const wsd = data?.workspace;
  const hc = data?.headcount;

  return (
    <div className="reports-widget">
      <WidgetHeader icon={BarChart3} title="Reports & Analytics" />

      {/* Controls — single row: from · to-icon · end · Save view · Export PDF */}
      <div className="report-no-print reports-controls">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="reports-ctrl" aria-label="From date" />
        <ArrowRight size={15} color={T.fg7} className="reports-ctrl-arrow" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="reports-ctrl" aria-label="To date" />
        <div className="reports-ctrl-spacer" />
        <button type="button" onClick={saveView} className="reports-ctrl reports-btn-ghost">
          <Save size={14} /> Save view
        </button>
        <button type="button" onClick={exportPdf} className="reports-btn-primary">
          <Printer size={14} /> Export PDF
        </button>
      </div>

      {/* Saved-view chips (apply + delete) */}
      {views.length > 0 && (
        <div className="report-no-print reports-chips">
          {views.map((v) => (
            <span key={v.id} className="reports-chip">
              <button type="button" onClick={() => applyView(v.id)} className="reports-chip-apply">{v.name}</button>
              <button type="button" onClick={() => deleteView(v.id)} title="Delete view" className="reports-chip-del"><Trash2 size={12} /></button>
            </span>
          ))}
        </div>
      )}

      {/* Printable report area */}
      <div className="report-print-area">
        <div className="report-print-header" style={{ marginBottom: 14 }}>
          <div style={{ font: `700 18px ${T.font}`, color: T.fg1 }}>Reports &amp; Analytics</div>
          <div style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>{fmtRange(from, to)}</div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: T.fg7, font: `400 13px ${T.font}` }}>Loading…</div>
        ) : !data ? (
          <div style={{ padding: 40, textAlign: "center", color: T.fg7, font: `400 13px ${T.font}` }}>No data.</div>
        ) : (
          <div className="reports-stack">
            {/* Attendance */}
            <Card icon={Clock} title="Attendance trends" accent={T.orange}>
              <KpiRow items={[
                { label: "Attendance rate", value: `${att.attendance_rate}%`, color: T.success600 },
                { label: "Late rate", value: `${att.late_rate}%`, color: T.danger },
                { label: "Avg hours/day", value: `${att.avg_hours}h` },
              ]} />
              <div style={{ font: `400 11px ${T.font}`, color: T.fg7 }}>
                {att.present_marks} check-ins over {att.days} day(s) · {att.active_employees} active employees
              </div>
              <Spark points={(att.trend || []).map((t) => ({ v: t.present }))} color={T.orange} />
            </Card>

            {/* Leave */}
            <Card icon={CalendarDays} title="Leave summary & liability" accent={T.purple}>
              <KpiRow items={[
                { label: "Outstanding (days)", value: lv.liability.outstanding_days, color: T.purple },
                { label: "Taken YTD", value: lv.liability.taken_ytd_days },
                { label: "Upcoming", value: `${lv.upcoming_days}d` },
              ]} />
              <div style={{ font: `600 11px ${T.font}`, color: T.fg5, margin: "4px 0 8px" }}>Approved days by type</div>
              <BarList rows={(lv.by_type || []).map((t) => ({ label: t.name, value: t.days, color: t.color }))} fmt={(v) => `${v}d`} />
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <Pill label={`${lv.by_status.approved} approved`} bg={T.success50} fg={T.success600} />
                <Pill label={`${lv.by_status.pending} pending`} bg="#fef3c7" fg="#92400e" />
                <Pill label={`${lv.by_status.rejected} rejected`} bg="#fee2e2" fg="#b91c1c" />
              </div>
            </Card>

            {/* Workspace */}
            <Card icon={Banknote} title="Workspace profitability" accent={T.success600}>
              <KpiRow items={[
                { label: "Revenue", value: money(wsd.totals.revenue), color: T.success600 },
                { label: "Cost", value: money(wsd.totals.cost), color: T.danger },
                { label: "Margin", value: `${wsd.totals.margin}%`, color: T.orange },
              ]} />
              <div style={{ font: `600 11px ${T.font}`, color: T.fg5, margin: "4px 0 8px" }}>Profit by project</div>
              <BarList
                rows={(wsd.projects || []).slice(0, 6).map((p) => ({ label: p.name, value: p.profit, color: p.color }))}
                max={Math.max(1, ...(wsd.projects || []).map((p) => Math.abs(p.profit)))}
                fmt={money}
              />
              {wsd.totals.pending_hours > 0 && (
                <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 8 }}>{wsd.totals.pending_hours}h pending approval (excluded)</div>
              )}
            </Card>

            {/* Headcount */}
            <Card icon={Users} title="Headcount & workforce" accent={T.info600}>
              <KpiRow items={[
                { label: "Active", value: hc.active_headcount },
                { label: "Joiners", value: hc.joiners, color: T.success600 },
                { label: "Exits", value: hc.exits, color: T.danger },
              ]} />
              <div style={{ font: `600 11px ${T.font}`, color: T.fg5, margin: "4px 0 8px" }}>By department</div>
              <BarList rows={(hc.by_department || []).map((d) => ({ label: d.label, value: d.count }))} color={T.info} />
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                {(hc.by_type || []).map((t) => <Pill key={t.label} label={`${t.label}: ${t.count}`} bg={T.bgHead} fg={T.fg5} />)}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

const Pill = ({ label, bg, fg }) => (
  <span style={{ padding: "3px 10px", borderRadius: 9999, background: bg, color: fg, font: `600 11px ${T.font}`, whiteSpace: "nowrap" }}>{label}</span>
);
